import { Icon } from '@shared/ui';
import clsx from 'clsx';
import type { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';

type NavLinkProps = {
  icon: string;
  route: string;
  label: string;
  /**
   * If true, only matches exact route (not sub-routes).
   * Used for routes like '/admin' that should only match exactly.
   */
  exactMatch?: boolean;
  /** Tighter row for navs long enough that the default height forces a scroll. */
  compact?: boolean;
  /** Mark active from the outside when the caller resolves it (e.g. longest-match wins). */
  isActiveOverride?: boolean;
};

const NavLink: FC<NavLinkProps> = ({
  icon,
  route,
  label,
  exactMatch = false,
  compact = false,
  isActiveOverride,
}) => {
  const location = useLocation();
  const matchesLocation = exactMatch
    ? location.pathname === route
    : location.pathname === route || location.pathname.startsWith(`${route}/`);
  const isActive = isActiveOverride ?? matchesLocation;

  return (
    <Link
      aria-current={isActive ? 'page' : undefined}
      aria-label={label}
      className={clsx(
        'flex w-full items-center no-underline transition-colors hover:no-underline',
        compact ? 'gap-2.5 rounded-[11px] p-1' : 'gap-3 rounded-[14px] p-2',
        isActive ? 'bg-sage-400' : 'hover:bg-white/10'
      )}
      to={route}
    >
      <div
        className={clsx(
          'flex items-center justify-center',
          compact ? 'size-8 rounded-[11px]' : 'size-10 rounded-[14px]',
          isActive ? 'bg-sage-400 text-black' : 'text-sage-200'
        )}
      >
        <Icon className={clsx(compact && 'text-[20px]', isActive ? 'text-black' : 'text-sage-200')}>
          {icon}
        </Icon>
      </div>
      <span
        className={clsx(
          'font-medium no-underline',
          compact ? 'text-[13px]' : 'text-sm',
          isActive ? 'text-black' : 'text-white'
        )}
      >
        {label}
      </span>
    </Link>
  );
};

export default NavLink;
